import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavbarProps {
  alwaysShowBg?: boolean;
}

export default function Navbar({ alwaysShowBg = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (alwaysShowBg) {
      setScrolled(true);
      return;
    }

    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [alwaysShowBg]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setSearchOpen(false);
      setSearchTerm('');
      navigate(`/category/all?search=${encodeURIComponent(searchTerm.trim())}`);
    }
  }, [searchTerm, navigate]);

  const navLinks = [
    { path: '/', label: '首页' },
    { path: '/category/all', label: '电影库' },
    { path: '/category/tvseries', label: '电视剧' },
    { path: '/category/animation', label: '动漫' },
    { path: '/about', label: '关于我们' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || alwaysShowBg
          ? 'bg-background-100/95 backdrop-blur-md shadow-[0_1px_0_0_rgba(255,255,255,0.05)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <nav className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <div className="w-9 h-9 flex items-center justify-center">
              <i className="ri-movie-2-line text-2xl text-primary-500 group-hover:text-primary-400 transition-colors"></i>
            </div>
            <span className="font-heading text-xl text-foreground-100 whitespace-nowrap group-hover:text-primary-400 transition-colors">
              奇优影院
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = link.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                    isActive
                      ? 'text-primary-500 bg-primary-500/10 font-medium'
                      : 'text-foreground-400 hover:text-foreground-100 hover:bg-background-200/50'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-100 hover:bg-background-200/50 transition-colors"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <Link
              to="/category/all"
              className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-play-circle-line text-base"></i>
              </span>
              立即观影
            </Link>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-100 transition-colors"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-300 hover:text-foreground-100 transition-colors"
              aria-label="菜单"
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </nav>

        {searchOpen && (
          <div className="pb-3 px-1">
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-sm text-foreground-500"></i>
                </span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="搜索电影、电视剧..."
                  className="w-full pl-9 pr-4 py-2.5 bg-background-200/60 border border-background-300/50 rounded-lg text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
              >
                搜索
              </button>
            </form>
          </div>
        )}

        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/30 pt-3">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => {
                const isActive = link.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(link.path);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                      isActive
                        ? 'text-primary-500 bg-primary-500/10 font-medium'
                        : 'text-foreground-400 hover:text-foreground-100 hover:bg-background-200/50'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <Link
                to="/category/all"
                className="mt-2 flex items-center gap-1.5 px-3 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-line text-base"></i>
                </span>
                立即观影
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}